import { useEffect, useMemo, useRef, useState } from 'react'
import { logger } from '@/utils/logger';
import { matchSorter } from 'match-sorter'
import { useNavigate } from 'react-router-dom'
import { Command, createCommandsRegistry, getCommandCategories } from '../../config/commands'
import {
  buildEntitySearchCommands,
  entitySearchCategories,
  ENTITY_RESULTS_PER_TYPE,
} from '../../config/entitySearchCommands'
import { useCommandPalette } from '../../hooks/useCommandPalette'
import { useGlobalSearch } from '../../hooks/useGlobalSearch'
import { useKeyboardShortcut } from '../../hooks/useKeyboardShortcut'
import { useTheme } from '../../contexts/ThemeContext'
import { useProject } from '../../contexts/ProjectContext'
import { useTerminology } from '../../hooks/useTerminology'
import { CommandItem } from './CommandItem'
import { CreateFailureModeModal } from '../modals/CreateFailureModeModal'
import { CreateComponentModal } from '../CreateComponentModal/CreateComponentModal'
import { exportFMEAReport } from '../../api/pdf-export'
import { exportFMEAToExcel } from '../../utils/excelExport'
import './CommandPalette.css'

interface CommandPaletteProps {
  isOpen: boolean
  onClose: () => void
  onOpenShortcutsHelp?: () => void
}

const MAX_RESULTS = 20

/**
 * Command Palette - Fuzzy search for commands with keyboard navigation
 *
 * Features:
 * - Fuzzy search with match-sorter
 * - Keyboard navigation (↑↓ arrows, Enter, Esc)
 * - Recent commands
 * - Category grouping
 * - Visual shortcut hints
 * - Create modals (failure mode, component, user story)
 */
export function CommandPalette({
  isOpen,
  onClose,
  onOpenShortcutsHelp,
}: CommandPaletteProps) {
  const navigate = useNavigate()
  const { toggleTheme } = useTheme()
  const { activeProject } = useProject()
  const { t } = useTerminology()
  const inputRef = useRef<HTMLInputElement>(null)
  const resultsRef = useRef<HTMLDivElement>(null)

  // F8: debounced query for live entity search (avoid a request per keystroke)
  const [debouncedQuery, setDebouncedQuery] = useState('')

  // Modal state
  const [showFailureModeModal, setShowFailureModeModal] = useState(false)
  const [showComponentModal, setShowComponentModal] = useState(false)

  const {
    searchQuery,
    selectedIndex,
    recentCommands,
    updateSearchQuery,
    selectNext,
    selectPrevious,
    setSelected,
    executeCommand,
  } = useCommandPalette()

  // The palette's visibility is owned by App via the `onClose` prop, but the
  // hook's own executeCommand only tracks recents + flips the hook's (unused)
  // internal open flag. Wrap it so selecting ANY command (route launcher,
  // action, or a live entity match) also closes the palette.
  const runCommand = (command: Command) => {
    executeCommand(command)
    onClose()
  }

  // Create commands registry
  const commands = createCommandsRegistry(navigate, {
    openCommandPalette: () => {}, // No-op, already open
    openShortcutsHelp: onOpenShortcutsHelp,
    toggleTheme: () => {
      toggleTheme()
      onClose() // Close command palette after toggle
    },
    exportToPDF: () => {
      onClose()
      exportFMEAReport().catch((error) => {
        logger.error('PDF export failed', error, { module: 'CommandPalette' });
      })
    },
    exportToExcel: () => {
      onClose()
      exportFMEAToExcel().catch((error) => {
        logger.error('Excel export failed', error, { module: 'CommandPalette' });
      })
    },
    createComponent: () => {
      // Close command palette and open component modal
      onClose()
      setShowComponentModal(true)
    },
    createFailureMode: () => {
      // Close command palette and open failure mode modal
      onClose()
      setShowFailureModeModal(true)
    },
    focusSearch: () => {
      // UF-5: global search now lives in THIS palette, so "Focus Search" simply
      // (re)focuses the palette's own input. The old code closed the palette and
      // queried `[data-search-input]`, an attribute that no longer exists in the
      // DOM (it lived in the disabled QuickNavSidebar) — a silent no-op. Keeping
      // the palette open and focusing its input is the sensible, working action.
      inputRef.current?.focus()
    },
  })

  // F8: debounce the query that drives the live entity search
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 250)
    return () => clearTimeout(handle)
  }, [searchQuery])

  // F8: live cross-entity search (Components, Failure Modes, Root Causes,
  // Mitigations, Requirements), scoped to the active project.
  const { data: searchResults } = useGlobalSearch(debouncedQuery, {
    limit: ENTITY_RESULTS_PER_TYPE,
    projectId: activeProject?.id,
    enabled: debouncedQuery.length >= 2,
  })

  const entityCommands = useMemo(
    () => buildEntitySearchCommands(searchResults, navigate, t),
    [searchResults, navigate, t]
  )

  // Static command matches (route launchers, actions, settings, help)
  const commandMatches = searchQuery.trim()
    ? matchSorter(commands, searchQuery, {
        keys: ['label', 'description', 'keywords', 'category'],
        threshold: matchSorter.rankings.CONTAINS,
      }).slice(0, MAX_RESULTS)
    : // Show recent commands if no search query
      commands
        .filter((cmd) => recentCommands.includes(cmd.id))
        .sort(
          (a, b) => recentCommands.indexOf(a.id) - recentCommands.indexOf(b.id)
        )
        .concat(commands.filter((cmd) => !recentCommands.includes(cmd.id)))
        .slice(0, MAX_RESULTS)

  // Combined, index-stable list: live entity matches first, then commands.
  // Keyboard navigation and selection index against this array.
  const filteredCommands = [...entityCommands, ...commandMatches]

  // Entity result groups (terminology-aware headings), rendered first
  const entityCategories = entitySearchCategories(entityCommands)

  // Group static commands by their built-in category
  const categories = getCommandCategories(commandMatches)
  const commandsByCategory: Record<string, Command[]> = {}
  categories.forEach((category) => {
    commandsByCategory[category] = commandMatches.filter(
      (cmd) => cmd.category === category
    )
  })

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus()
    }
  }, [isOpen])

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current) {
      const selectedId = filteredCommands[selectedIndex]?.id
      if (selectedId) {
        const selectedElement = resultsRef.current.querySelector(
          `[data-command-id="${selectedId}"]`
        ) as HTMLElement
        selectedElement?.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth',
        })
      }
    }
  }, [selectedIndex, filteredCommands])

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        selectNext(filteredCommands.length)
        break

      case 'ArrowUp':
        event.preventDefault()
        selectPrevious(filteredCommands.length)
        break

      case 'Enter':
        event.preventDefault()
        if (filteredCommands[selectedIndex]) {
          runCommand(filteredCommands[selectedIndex])
        }
        break

      case 'Escape':
        event.preventDefault()
        onClose()
        break
    }
  }

  // Close on backdrop click
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose()
    }
  }

  // Close on Escape key
  useKeyboardShortcut(['escape'], onClose, {
    preventDefault: true,
    ignoreInputs: false, // Allow Escape even in inputs
  })

  if (!isOpen) {
    return (
      <>
        {/* Render modals even when command palette is closed */}
        {showFailureModeModal && (
          <CreateFailureModeModal
            isOpen={showFailureModeModal}
            onClose={() => setShowFailureModeModal(false)}
          />
        )}
        {showComponentModal && (
          <CreateComponentModal
            onClose={() => setShowComponentModal(false)}
          />
        )}
      </>
    )
  }

  return (
    <>
      <div
        className="command-palette-backdrop"
        onClick={handleBackdropClick}
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
      >
        <div className="command-palette">
          <div className="command-palette-search">
            <span className="command-palette-search-icon" aria-hidden="true">
              🔍
            </span>
            <input
              ref={inputRef}
              type="text"
              className="command-palette-input"
              placeholder="Search components, failure modes, causes… or a command"
              value={searchQuery}
              onChange={(e) => updateSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              role="combobox"
              aria-expanded={true}
              aria-controls="command-list"
              aria-activedescendant={
                filteredCommands[selectedIndex]?.id || undefined
              }
              autoComplete="off"
            />
            <div className="command-palette-hint">
              <kbd>Esc</kbd> to close
            </div>
          </div>

          <div className="command-palette-results" ref={resultsRef}>
            {filteredCommands.length === 0 ? (
              <div className="command-palette-empty">
                <p>No commands found</p>
                <p className="command-palette-empty-hint">
                  Try searching for "dashboard", "create", or "export"
                </p>
              </div>
            ) : (
              <div>
                {!searchQuery.trim() && recentCommands.length > 0 && (
                  <div className="command-palette-section">
                    <div className="command-palette-section-header">
                      Recent Commands
                    </div>
                    <ul

                      role="listbox"
                      id="command-list"
                      className="command-palette-list"
                    >
                      {filteredCommands
                        .filter((cmd) => recentCommands.includes(cmd.id))
                        .slice(0, 5)
                        .map((command, index) => (
                          <CommandItem
                            key={command.id}
                            command={command}
                            isSelected={index === selectedIndex}
                            onClick={() => runCommand(command)}
                            onMouseEnter={() => setSelected(index)}
                          />
                        ))}
                    </ul>
                  </div>
                )}

                {/* F8: live entity matches, grouped by terminology-aware type */}
                {entityCategories.map((category) => {
                  const cmds = entityCommands.filter(
                    (cmd) => cmd.category === category
                  )
                  if (cmds.length === 0) return null

                  return (
                    <div
                      key={`entity-${category}`}
                      className="command-palette-section"
                    >
                      <div className="command-palette-section-header">
                        {category}
                      </div>
                      <ul
                        role="listbox"
                        id="command-list"
                        className="command-palette-list"
                      >
                        {cmds.map((command) => {
                          const globalIndex = filteredCommands.indexOf(command)
                          return (
                            <CommandItem
                              key={command.id}
                              command={command}
                              isSelected={globalIndex === selectedIndex}
                              onClick={() => runCommand(command)}
                              onMouseEnter={() => setSelected(globalIndex)}
                            />
                          )
                        })}
                      </ul>
                    </div>
                  )
                })}

                {categories.map((category) => {
                  const cmds = commandsByCategory[category]
                  if (cmds.length === 0) return null

                  return (
                    <div key={category} className="command-palette-section">
                      <div className="command-palette-section-header">
                        {category}
                      </div>
                      <ul
  
                        role="listbox"
                        id="command-list"
                        className="command-palette-list"
                      >
                        {cmds.map((command) => {
                          const globalIndex = filteredCommands.indexOf(command)
                          return (
                            <CommandItem
                              key={command.id}
                              command={command}
                              isSelected={globalIndex === selectedIndex}
                              onClick={() => runCommand(command)}
                              onMouseEnter={() => setSelected(globalIndex)}
                            />
                          )
                        })}
                      </ul>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Render modals */}
      {showFailureModeModal && (
        <CreateFailureModeModal
          isOpen={showFailureModeModal}
          onClose={() => setShowFailureModeModal(false)}
        />
      )}
      {showComponentModal && (
        <CreateComponentModal
          onClose={() => setShowComponentModal(false)}
        />
      )}
    </>
  )
}
